/**
 * TOTP enrolment — the step that was completely unbuilt.
 *
 * There is no Entra tenant in this deployment, so the break-glass local account is the only
 * way in, and `nextLoginStep` sends it to `MFA_ENROLMENT` until it has an authenticator.
 * The sign-in page had only a code-entry box for an ALREADY-enrolled app, so this step had
 * no UI at all and the product was unusable. This screen is that UI.
 *
 * ── Three things must be on screen at once, and they are ─────────────────────
 *
 *  1. THE QR CODE, rendered from `otpauthUri` as an inline `<svg>` by `qrcode.react`
 *     (4.2.0, zero runtime dependencies). Not an image from a CDN: the deployment's CSP is
 *     `img-src 'self' data: blob:` with `default-src 'self'`, so a remote chart API would
 *     be blocked — and posting a TOTP secret to a third party to have it drawn would be
 *     indefensible regardless of what the CSP allowed.
 *
 *  2. THE SECRET AS SELECTABLE TEXT. A break-glass account is signed into from wherever the
 *     outage is being handled, which is often a machine with no camera, or a remote session
 *     where the QR code cannot be scanned off the screen at all. Manual entry has to work.
 *
 *  3. THE EIGHT RECOVERY CODES, with copy and download, behind an explicit acknowledgement.
 *     `generateRecoveryCodes` bcrypt-hashes them and returns the plaintext exactly once —
 *     no endpoint can show them again, only replace the whole set. There is deliberately no
 *     email reset for this account (`auth.ts`: an email link "would be the weakest path into
 *     the highest-privilege account in the system"). Lose the phone AND the codes and the
 *     account is only recoverable by re-provisioning from the environment on a restart. The
 *     confirm button therefore stays disabled until the acknowledgement is ticked.
 *
 * ── Why `start` is called exactly once ───────────────────────────────────────
 *
 * `POST /auth/mfa/enrol/start` REGENERATES the secret and the recovery-code set on every
 * call and writes both to the user row. Calling it twice would invalidate codes the user
 * may already have written down, and would burn two of the five attempts the MFA rate
 * limiter allows per five minutes. React 18 StrictMode double-invokes effects in
 * development, so the call is guarded by a ref rather than by the effect's dependency list.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Check, Copy, Download, KeyRound, ShieldCheck } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

import { api } from '@frontend/api/client';
import { Button } from '@frontend/components/ui/Button';
import { Checkbox, FieldShell, Input } from '@frontend/components/ui/Field';
import { Banner } from '@frontend/components/ui/Page';

import { describeAuthFailure } from './authErrors';
import {
  readEnrolmentStarted,
  readLoginOutcome,
  unrecognisedOutcome,
  type EnrolmentStarted,
  type LoginOutcome,
} from './loginSteps';

interface Props {
  stepToken: string;
  /** The server's own description of the step. */
  message: string;
  onAdvance: (outcome: LoginOutcome) => void;
  /** Called when the step token is dead — the caller returns to the credentials form. */
  onRestart: (reason: string) => void;
}

export function MfaEnrolment({ stepToken, message, onAdvance, onRestart }: Props): JSX.Element {
  const [enrolment, setEnrolment] = useState<EnrolmentStarted | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState<'secret' | 'codes' | null>(null);
  const [busy, setBusy] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      try {
        const body = await api.post<unknown>('/auth/mfa/enrol/start', { stepToken });
        const parsed = readEnrolmentStarted(body);
        if (parsed === null) {
          setError(unrecognisedOutcome('POST /api/auth/mfa/enrol/start'));
          return;
        }
        setEnrolment(parsed);
      } catch (caught) {
        const failure = describeAuthFailure(
          caught,
          'MFA_ENROLMENT',
          'The authenticator setup could not be started and no reason was returned. Check your connection and sign in again.',
        );
        if (failure.restart) onRestart(failure.message);
        else setError(failure.message);
      }
    })();
  }, [stepToken, onRestart]);

  async function confirm(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (enrolment === null) return;
    setError(null);
    setCodeError(null);
    setBusy(true);
    const form = new FormData(event.currentTarget);
    try {
      const body = await api.post<unknown>('/auth/mfa/enrol/confirm', {
        stepToken: enrolment.stepToken,
        code: String(form.get('code') ?? '').replace(/[\s-]/g, ''),
      });
      const outcome = readLoginOutcome(body);
      if (outcome === null) {
        setError(unrecognisedOutcome('POST /api/auth/mfa/enrol/confirm'));
        return;
      }
      onAdvance(outcome);
    } catch (caught) {
      const failure = describeAuthFailure(
        caught,
        'MFA_ENROLMENT',
        'The code could not be checked and no reason was returned. Check your connection and try again.',
      );
      if (failure.restart) {
        onRestart(failure.message);
        return;
      }
      setError(failure.message);
      setCodeError(failure.fieldErrors['code'] ?? null);
    } finally {
      setBusy(false);
    }
  }

  async function copy(what: 'secret' | 'codes'): Promise<void> {
    if (enrolment === null) return;
    const payload = what === 'secret' ? enrolment.secret : enrolment.recoveryCodes.join('\n');
    try {
      await navigator.clipboard.writeText(payload);
      setCopied(what);
      window.setTimeout(() => setCopied(null), 2500);
    } catch {
      // Clipboard access is refused on an insecure origin and by some managed browsers.
      // The value is on screen and selectable, so this is a convenience, not the mechanism.
      setError(
        'The browser would not let AgentERP write to the clipboard. Select the text on screen and copy it manually.',
      );
    }
  }

  function download(): void {
    if (enrolment === null) return;
    const contents = [
      'AgentERP — recovery codes',
      `Account: ${enrolment.otpauthUri.split('/').pop()?.split('?')[0] ?? 'AgentERP account'}`,
      `Generated: ${new Date().toISOString()}`,
      '',
      'Each code can be used ONCE, in place of your authenticator code, to sign in.',
      'These codes are shown only at enrolment and cannot be retrieved later.',
      'Store them somewhere you can reach WITHOUT this system and WITHOUT your phone.',
      '',
      ...enrolment.recoveryCodes,
      '',
    ].join('\r\n');
    const blob = new Blob([contents], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'agenterp-recovery-codes.txt';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Revoked on the next tick — Safari cancels the download if it goes immediately.
    window.setTimeout(() => URL.revokeObjectURL(url), 4000);
  }

  if (enrolment === null) {
    return (
      <div data-testid="mfa-enrolment-loading">
        <h1 className="mb-1 text-section font-semibold text-content">Set up your authenticator</h1>
        {error === null ? (
          <p className="text-row text-content-secondary">Preparing your authenticator setup…</p>
        ) : (
          <Banner tone="error" data-testid="login-error">
            {error}
          </Banner>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={(event) => void confirm(event)} data-testid="mfa-enrolment-form">
      <h1 className="mb-1 text-section font-semibold text-content">Set up your authenticator</h1>
      <p className="mb-4 text-row text-content-secondary" data-testid="step-message">
        {message}
      </p>

      {error !== null && (
        <Banner tone="error" className="mb-4" data-testid="login-error">
          {error}
        </Banner>
      )}

      {/* 1 — Scan */}
      <section className="mb-5">
        <h2 className="mb-2 text-row font-semibold text-content">1. Scan this with your authenticator app</h2>
        <div className="flex flex-col items-center gap-3 rounded-md border border-line bg-surface p-4">
          {/* Inline SVG, generated in the browser. No network request, no third party. */}
          <QRCodeSVG
            value={enrolment.otpauthUri}
            size={184}
            level="M"
            marginSize={2}
            bgColor="#ffffff"
            fgColor="#000000"
            title="Authenticator setup QR code for your AgentERP account"
            data-testid="mfa-qr"
          />
          <p className="text-center text-body text-content-secondary">
            Microsoft Authenticator, Google Authenticator, 1Password, Bitwarden — any TOTP app will do.
          </p>
        </div>

        <div className="mt-3 rounded-md border border-line bg-surface-subtle p-3">
          <p className="mb-1.5 flex items-center gap-1.5 text-body font-medium text-content">
            <KeyRound aria-hidden className="h-3.5 w-3.5" />
            No camera? Add the account by hand with this setup key
          </p>
          <div className="flex items-center gap-2">
            <code
              className="flex-1 select-all break-all rounded border border-line-control bg-surface px-2 py-1.5 text-body tracking-[0.08em] text-content"
              data-testid="mfa-secret"
            >
              {enrolment.secret}
            </code>
            <Button
              size="sm"
              onClick={() => void copy('secret')}
              leadingIcon={copied === 'secret' ? <Check aria-hidden className="h-3.5 w-3.5" /> : <Copy aria-hidden className="h-3.5 w-3.5" />}
              data-testid="copy-mfa-secret"
            >
              {copied === 'secret' ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <p className="mt-1.5 text-body text-content-secondary">
            Time-based, 6 digits, 30-second interval, SHA-1 — the defaults in every authenticator app.
          </p>
        </div>
      </section>

      {/* 2 — Recovery codes */}
      <section className="mb-5">
        <h2 className="mb-2 text-row font-semibold text-content">2. Save your recovery codes</h2>
        <Banner tone="warning" className="mb-2">
          These {enrolment.recoveryCodes.length} codes are shown <strong>once</strong> and can never be
          retrieved again. Each one signs you in a single time if your authenticator is lost. There is no
          email reset for this account — without the app and without these codes, nobody can sign in.
        </Banner>
        <ul
          className="grid grid-cols-2 gap-1.5 rounded-md border border-line bg-surface p-3 font-mono text-body text-content"
          data-testid="recovery-codes"
        >
          {enrolment.recoveryCodes.map((code) => (
            <li key={code} className="select-all tracking-[0.04em]">
              {code}
            </li>
          ))}
        </ul>
        <div className="mt-2 flex gap-2">
          <Button
            size="sm"
            onClick={() => void copy('codes')}
            leadingIcon={copied === 'codes' ? <Check aria-hidden className="h-3.5 w-3.5" /> : <Copy aria-hidden className="h-3.5 w-3.5" />}
            data-testid="copy-recovery-codes"
          >
            {copied === 'codes' ? 'Copied' : 'Copy all'}
          </Button>
          <Button
            size="sm"
            onClick={download}
            leadingIcon={<Download aria-hidden className="h-3.5 w-3.5" />}
            data-testid="download-recovery-codes"
          >
            Download
          </Button>
        </div>
        <div className="mt-3">
          <Checkbox
            checked={acknowledged}
            onChange={(event) => setAcknowledged(event.currentTarget.checked)}
            data-testid="ack-recovery-codes"
            label="I have saved these recovery codes somewhere I can reach without this system"
            description="Printed, in a password manager, or in a safe. Not in this browser."
          />
        </div>
      </section>

      {/* 3 — Confirm */}
      <section>
        <h2 className="mb-2 text-row font-semibold text-content">3. Confirm the app is working</h2>
        <FieldShell
          label="Code from your authenticator"
          htmlFor="enrol-code"
          required
          fieldKey="code"
          error={codeError}
          hint="Six digits. They change every 30 seconds."
        >
          <Input
            id="enrol-code"
            name="code"
            rawName
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9 ]*"
            maxLength={9}
            required
            invalid={codeError !== null}
            placeholder="000000"
            className="tabular tracking-[0.3em]"
            data-testid="input-mfa-code"
          />
        </FieldShell>
        <Button
          type="submit"
          variant="primary"
          fullWidth
          className="mt-4"
          isLoading={busy}
          disabled={!acknowledged}
          leadingIcon={<ShieldCheck aria-hidden className="h-3.5 w-3.5" />}
          data-testid="submit-mfa-enrolment"
        >
          Confirm and finish signing in
        </Button>
        {!acknowledged && (
          <p className="mt-2 text-body text-content-secondary" data-testid="ack-required-note">
            Confirm you have saved the recovery codes above before finishing — this is the only time they
            are shown.
          </p>
        )}
      </section>
    </form>
  );
}
